import React from 'react';
import { DatePicker } from 'antd';
import cx from 'classnames';
import dayjs, { Dayjs } from 'dayjs';
import { RangeValue } from 'rc-picker/lib/interface';
import { appConfig } from '../../../settings';
import { IDateRange, IFilterParams } from '../../../interfaces';
import { getAvailableMinDate, getDefaultDateRate } from '../../../instruments';
import { tCustom, tI } from '../../../translate';
import { Catcher } from '../../Catcher';
import Styles from '../m_styles.less';

type IPresetsItem = { label: React.ReactNode; value: [Dayjs, Dayjs] };
type IPresentList = IPresetsItem[];
type IPresetsMonthItem = IPresetsItem & {
    moths: number;
};
type IPresentMonthList = IPresetsMonthItem[];

type IUsePresentList = {
    maximumMonths?: number;
    filterParams?: IFilterParams;
};

const usePresentList = ({ maximumMonths, filterParams }: IUsePresentList): IPresentList => {
    const presentMonthList: IPresentMonthList = [
        {
            label: tI('Today'),
            value: [dayjs(), dayjs()],
            moths: 0,
        },
        {
            label: tI('This Week'),
            value: [dayjs().startOf('week'), dayjs().endOf(filterParams?.isDateRangeMaxDayToDay ? 'day' : 'week')],
            moths: 1,
        },
        {
            label: tI('This Month'),
            value: [dayjs().startOf('month'), dayjs().endOf(filterParams?.isDateRangeMaxDayToDay ? 'day' : 'month')],
            moths: 1,
        },
        {
            label: tI('Previous Month'),
            value: [dayjs().subtract(1, 'months').startOf('month'), dayjs().subtract(1, 'months').endOf('month')],
            moths: 2,
        },
        {
            label: tCustom(`Last X month`, { month: 3 }),
            value: [
                dayjs().subtract(3, 'M').startOf('M'),
                dayjs().endOf(filterParams?.isDateRangeMaxDayToDay ? 'day' : 'month'),
            ],
            moths: 3,
        },
        {
            label: tCustom(`Last X month`, { month: 6 }),
            value: [
                dayjs().subtract(6, 'M').startOf('M'),
                dayjs().endOf(filterParams?.isDateRangeMaxDayToDay ? 'day' : 'month'),
            ],
            moths: 6,
        },
        {
            label: tCustom(`Last X month`, { month: 12 }),
            value: [
                dayjs().subtract(12, 'M').startOf('M'),
                dayjs().endOf(filterParams?.isDateRangeMaxDayToDay ? 'day' : 'month'),
            ],
            moths: 12,
        },
    ];
    const hasMonth = presentMonthList.map((i: IPresetsMonthItem) => i.moths);
    const calcMaxMonth = maximumMonths ?? appConfig.maximumMonthsForRequest;
    if (!hasMonth.includes(calcMaxMonth)) {
        presentMonthList.push({
            label: tCustom(`Last X month`, { month: calcMaxMonth }),
            value: [
                dayjs().subtract(calcMaxMonth, 'M').startOf('M'),
                dayjs().endOf(filterParams?.isDateRangeMaxDayToDay ? 'day' : 'month'),
            ],
            moths: calcMaxMonth,
        });
    }

    return presentMonthList
        .sort((a: IPresetsMonthItem, b: IPresetsMonthItem) => a.moths - b.moths)
        .filter((i: IPresetsMonthItem) => maximumMonths === undefined || i.moths - 1 <= maximumMonths)
        .map((i: IPresetsMonthItem) => ({ value: i.value, label: i.label }));
};

interface IFilterDateRangeProps {
    dateRange?: IDateRange;
    maximumMonths?: number;
    filterParams?: IFilterParams;
    onChange?: (val: IDateRange) => void;
    labelSpacing?: number | string;
}

export const FilterDateRange: React.FC<IFilterDateRangeProps> = ({
    onChange,
    filterParams,
    dateRange,
    maximumMonths,
    labelSpacing = 0,
}: IFilterDateRangeProps) => {
    const { RangePicker } = DatePicker;
    const startDate = dateRange?.startDate ? dayjs(dateRange.startDate, appConfig.formatDate) : null;
    const endDate = dateRange?.endDate ? dayjs(dateRange.endDate, appConfig.formatDate) : null;
    const availableMinDate = getAvailableMinDate(maximumMonths);
    const toDate = dayjs();

    const _setDateRange = (_: RangeValue<Dayjs>, dates: Array<string>): void => {
        const { startDate, endDate } = getDefaultDateRate(filterParams);
        onChange && onChange({ startDate: dates[0] || startDate, endDate: dates[1] || endDate });
    };

    const disabledDate = (current: Dayjs): boolean => {
        if (filterParams?.isDateRangeMinDayOn && current.isBefore(availableMinDate)) {
            return true;
        } else if (filterParams?.isDateRangeMaxDayToDay && current.isAfter(toDate)) {
            return true;
        }
        return false;
    };

    const presets = usePresentList({ maximumMonths, filterParams });

    return (
        <Catcher>
            <div className={Styles.labelFieldPrefix} style={{ padding: labelSpacing }}>
                {tI('Date Filter')}
            </div>
            <RangePicker
                className={cx(Styles.fieldFull, Styles.dateRange)}
                disabledDate={disabledDate}
                onChange={_setDateRange}
                value={[startDate, endDate]}
                placeholder={[tI('from'), tI('to')]}
                presets={presets}
            />
        </Catcher>
    );
};
